/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import apiClient from './client';

/**
 * Owner-specific settings stored as JSON per owner.
 * The JSON schema is flexible — adding a new key here automatically
 * renders a new input on the Settings page (no UI changes needed).
 */
/** Driver license requirement when creating a new driver. */
export type DriverLicenseRequirement = 'none' | 'front' | 'both';

export interface GuiPageConfig {
  visible: boolean;
  title: string;
}

export interface GuiSettings {
  pages: Record<string, GuiPageConfig>;
}

export const DEFAULT_GUI_PAGES: Record<string, GuiPageConfig> = {
  // Dashboard
  dashboard:                      { visible: true, title: 'Dashboard' },
  // Bookings
  bookings:                       { visible: true, title: 'Bookings' },
  // Vehicles
  allVehicles:                    { visible: true, title: 'All Vehicles' },
  rates:                          { visible: true, title: 'Rates' },
  pricingSeasons:                 { visible: true, title: 'Pricing Seasons' },
  // Closed by default: a new owner starts without Car Sharing, Commercial Cars and
  // TLC Plates. An admin can still switch any of them on per owner from Settings →
  // Page Visibility; owners who already have GUI settings saved keep their own values.
  carSharing:                     { visible: false, title: 'Car Sharing' },
  commercialCars:                 { visible: false, title: 'Commercial Cars' },
  licensePlates:                  { visible: false, title: 'License Plates' },
  gpsTracking:                    { visible: true, title: 'GPS Tracking' },
  // Drivers / Users
  users:                          { visible: true, title: 'Users' },
  createUser:                     { visible: false, title: 'Create User' },
  // The renter's own saved cards. It was already filtered by page visibility but had no entry here,
  // so the filter could never say no.
  stripeCards:                    { visible: true, title: 'Driver Cards' },
  // Partnership
  createOwner:                    { visible: true, title: 'Create Owner' },
  partners:                       { visible: true, title: 'Partners' },
  // Payments
  charges:                        { visible: true, title: 'Charges' },
  tolls:                          { visible: true, title: 'Tolls' },
  violations:                     { visible: true, title: 'Violations' },
  failedPayments:                 { visible: true, title: 'Failed Payments' },
  successfulPayments:             { visible: true, title: 'Successful Payments' },
  stripeDashboard:                { visible: false, title: 'Stripe Dashboard' },
  disputes:                       { visible: true, title: 'Disputes' },
  // Debts the reminder series could not collect, waiting on a decision to refer them to an agency.
  collections:                    { visible: false, title: 'Collections' },
  // Tolls and Violations (toll providers)
  tollTransactions:               { visible: true, title: 'Toll Transactions' },
  // Tesla Supercharger sessions billed to the owner and rebilled to renters.
  supercharger:                   { visible: true, title: 'Supercharger' },
  // Reports
  accountStatementReport:         { visible: true, title: 'Account Statement Report' },
  annualReport:                   { visible: true, title: 'Annual Report' },
  platformAccountStatementReport: { visible: true, title: 'Platform Account Statement' },
  platformAnnualReport:           { visible: true, title: 'Platform Annual Report' },
  // Bookings from a connected P2P platform (Turo and friends) — one nav entry per platform, all of
  // them under this single switch.
  p2pBookings:                    { visible: true, title: 'P2P Bookings' },
  // Company
  locations:                      { visible: true, title: 'Locations' },
  // The owner's own Stripe cards, and the in-app guides. Both were always-on links with no way to
  // close them; they default to visible so adding the switch changes nothing until somebody uses it.
  myStripe:                       { visible: true, title: 'My Stripe' },
  guides:                         { visible: true, title: 'User Guides' },
  settings:                       { visible: true, title: 'Settings' },
  design:                         { visible: true, title: 'Storefront Design' },
  // Billing
  invoicesToPay:                  { visible: true, title: 'Invoices to Pay' },
  // SMS and Mail
  smsLog:                         { visible: true, title: 'SMS Log' },
  messages:                       { visible: true, title: 'Messages' },
  // Administration
  owners:                         { visible: true, title: 'Owners' },
  tollDistributions:              { visible: true, title: 'Toll Distributions' },
  adminSettings:                  { visible: true, title: 'Admin Settings' },
  platformStripeDashboard:        { visible: true, title: 'Platform Stripe Dashboard' },
  commonLocations:                { visible: true, title: 'Shared Locations' },
  plugins:                        { visible: true, title: 'Plugins' },
  pluginsSettings:                { visible: true, title: 'Plugins Settings' },
  scrapeWorker:                   { visible: true, title: 'Scrape Worker' },
  platformInvoices:               { visible: true, title: 'Platform Invoices' },
  changeLog:                      { visible: true, title: 'Change History' },
  integrations:                   { visible: true, title: 'Integrations' },
};

/**
 * Page-visibility settings grouped by the left-nav section they belong to,
 * so the Settings UI mirrors the sidebar layout. `titleKey` is an i18n key;
 * each `keys` entry references a key in {@link DEFAULT_GUI_PAGES}.
 */
export interface GuiPageGroup {
  titleKey: string;
  keys: string[];
}

export const GUI_PAGE_GROUPS: GuiPageGroup[] = [
  { titleKey: 'nav.dashboard',              keys: ['dashboard'] },
  { titleKey: 'nav.sections.bookings',      keys: ['bookings', 'p2pBookings'] },
  { titleKey: 'nav.sections.vehicles',      keys: ['allVehicles', 'rates', 'pricingSeasons', 'carSharing', 'commercialCars', 'licensePlates', 'gpsTracking'] },
  { titleKey: 'nav.sections.users',         keys: ['users', 'createUser', 'stripeCards'] },
  { titleKey: 'nav.sections.partnership',   keys: ['createOwner', 'partners'] },
  { titleKey: 'nav.sections.payments',      keys: ['charges', 'tolls', 'violations', 'failedPayments', 'successfulPayments', 'stripeDashboard', 'disputes', 'collections'] },
  { titleKey: 'nav.sections.tollProviders', keys: ['tollTransactions', 'supercharger'] },
  { titleKey: 'nav.sections.reports',       keys: ['accountStatementReport', 'annualReport', 'platformAccountStatementReport', 'platformAnnualReport'] },
  { titleKey: 'nav.sections.company',       keys: ['locations', 'settings', 'design', 'myStripe', 'guides'] },
  { titleKey: 'nav.sections.billing',       keys: ['invoicesToPay'] },
  { titleKey: 'nav.sections.sms',           keys: ['smsLog', 'messages'] },
  { titleKey: 'nav.sections.administration', keys: ['owners', 'tollDistributions', 'adminSettings', 'platformStripeDashboard', 'commonLocations', 'plugins', 'pluginsSettings', 'scrapeWorker', 'platformInvoices', 'changeLog', 'integrations'] },
];

/**
 * GPS alert delivery preferences (per owner).
 * Read by the GPS ingestion/worker pipeline when a tracker event fires.
 * Each alert type can independently notify the owner and/or the active renter
 * (driver) over SMS and/or email.
 */
export interface GpsAlertChannels {
  /** Send an SMS to the vehicle owner. */
  smsOwner: boolean;
  /** Send an SMS to the active renter (driver). */
  smsDriver: boolean;
  /** Send an email to the vehicle owner. */
  emailOwner: boolean;
  /** Send an email to the active renter (driver). */
  emailDriver: boolean;
}

export interface GpsAlertsSettings {
  /** Ignition turned on. */
  ignitionOn: GpsAlertChannels;
  /** Ignition turned off. */
  ignitionOff: GpsAlertChannels;
  /** Ignition stayed off long enough → trip considered ended. The idle threshold is a
   *  platform-global setting (the worker is owner-agnostic), so it is not per-owner here. */
  tripEnded: GpsAlertChannels;
  /** Vehicle moved while ignition is off → likely being towed. */
  towed: GpsAlertChannels;
  /** Speed exceeded the road's posted/legal limit. The thresholds, sustained-window and cooldown are
   *  platform-global worker settings, so they are not per-owner here. */
  speeding: GpsAlertChannels;
  /** Speed exceeded the absolute "reckless" ceiling regardless of the road. Separate from `speeding`
   *  because it is a far more serious event and an owner may want it delivered to different people. */
  recklessSpeed: GpsAlertChannels;
  /** The tracker stopped reporting for the platform-global silence window — the closest signal we have
   *  to hardware removal. Fleet-wide silence is suppressed as an outage rather than fanned out. */
  offline: GpsAlertChannels;
  /** Engine running with the vehicle stationary for the platform-global idling window. */
  idling: GpsAlertChannels;
  /** Engine running but no usable satellite fix — pulled antenna, failing device, or a jammer. A parked
   *  vehicle losing its fix is normal and never reported. */
  gpsLost: GpsAlertChannels;
}

const emptyGpsChannels = (): GpsAlertChannels => ({
  smsOwner: false,
  smsDriver: false,
  emailOwner: false,
  emailDriver: false,
});

export const DEFAULT_GPS_ALERTS: GpsAlertsSettings = {
  ignitionOn: emptyGpsChannels(),
  ignitionOff: emptyGpsChannels(),
  tripEnded: emptyGpsChannels(),
  towed: emptyGpsChannels(),
  speeding: emptyGpsChannels(),
  recklessSpeed: emptyGpsChannels(),
  offline: emptyGpsChannels(),
  idling: emptyGpsChannels(),
  gpsLost: emptyGpsChannels(),
};

/** Merge a (possibly partial / missing) saved value with the defaults. */
export function mergeGpsAlerts(saved: unknown): GpsAlertsSettings {
  const s = (saved || {}) as Partial<GpsAlertsSettings>;
  return {
    ignitionOn: { ...DEFAULT_GPS_ALERTS.ignitionOn, ...s.ignitionOn },
    ignitionOff: { ...DEFAULT_GPS_ALERTS.ignitionOff, ...s.ignitionOff },
    tripEnded: { ...DEFAULT_GPS_ALERTS.tripEnded, ...s.tripEnded },
    towed: { ...DEFAULT_GPS_ALERTS.towed, ...s.towed },
    speeding: { ...DEFAULT_GPS_ALERTS.speeding, ...s.speeding },
    recklessSpeed: { ...DEFAULT_GPS_ALERTS.recklessSpeed, ...s.recklessSpeed },
    offline: { ...DEFAULT_GPS_ALERTS.offline, ...s.offline },
    idling: { ...DEFAULT_GPS_ALERTS.idling, ...s.idling },
    gpsLost: { ...DEFAULT_GPS_ALERTS.gpsLost, ...s.gpsLost },
  };
}

export interface OwnerSettingsJSON {
  registrationFee?: number;
  /** Per-owner monthly fee per active GPS navigator, charged on the last day of each month. Admin-set.
   *  Blank/absent ("" or unset) → inherit the configurable platform default; a number overrides; 0 waives.
   *  Stored as a string because the settings UI allows a blank value. Read by GpsNavigatorFeeService. */
  gpsNavigatorMonthlyFee?: number | string;
  /** "Pay per plate" billing MODE. When true the owner pays the platform a per-plate-per-day fee (billed
   *  through the normal owner-billing cycle) and the per-category payment collectors are all forced off.
   *  Read by PlateFeeService. */
  payPerPlate?: boolean;
  /** Per-owner override for the per-plate PER-DAY fee (dollars and cents). Absent → platform default ($5.00).
   *  Only applies while payPerPlate is true. Read by PlateFeeService. */
  perPlatePerDayFee?: number | string;
  minSecurityDeposit?: number;        // minimum security deposit for new cars (default 400)
  signUpFlow?: string;
  smsDeliveryMethod?: string;
  // "twilio" (SMS) | "email" | "both" (SMS + email) | "qr" — takes priority over smsDeliveryMethod
  messageDeliveryMethod?: string;
  // Tri-state agreement gates: "no" | "required" (sent, non-blocking) | "mandatory" (blocks booking).
  // Legacy booleans still accepted (true → required, false → no) for back-compat on read.
  requireRentalAgreement?: boolean | 'no' | 'required' | 'mandatory';  // default "required"
  requireAgreementOnRegistration?: boolean | 'no' | 'required' | 'mandatory'; // default "required" (new-driver first contract)
  shortenedAgreement?: boolean;      // default false — use shortened rental agreement PDF
  sendAgreementOnlyToNewUsers?: boolean; // default false — skip agreement for returning users
  requireEmailVerification?: boolean;
  /** Bill an UNCERTAIN GPS-only toll (a gantry with a parallel free lane — low-confidence detection that
   *  the car on the adjacent free lane can false-match). Default false: uncertain GPS-only tolls are never
   *  charged; once the agency confirms the same toll it is billed regardless. Safeguard against over-billing
   *  the renter on a false GPS detection. Only matters when the owner bills from GPS. */
  chargeUncertainGpsTolls?: boolean;
  /** TEST OPTION, admin-set. Shows the Import button on Toll Transactions for this owner, which loads
   *  toll rows from an Excel/CSV file instead of scraping a provider. Absent/false = hidden, which is the
   *  default for every owner. Admins see the button regardless of this flag. Read by TollTransactionsPage.
   *  Imported rows are saved through the ordinary save path and become real charges, so it is meant for
   *  trying the pipeline out, not for routine data entry. */
  allowTestTollImport?: boolean;
  /** Auto-retry a transient-declined toll charge (do_not_honor, insufficient_funds, …) a few times over the
   *  following days. Default false — a declined toll stays Failed for manual handling. Read by BackgroundService. */
  tollRechargeEnabled?: boolean;
  /** Driver license requirement when creating a new driver:
   *  "none" = not required, "front" = front side required, "both" = both sides required.
   *  Default "both". */
  driverLicenseRequirement?: DriverLicenseRequirement;
  desiredPrice?: number;              // booking price charged at creation (default 1)
  defaultMinReservationDays?: number; // default minimum reservation days for new cars (default 7)
  defaultPricingPeriod?: number;      // default pricing period for new cars: 0=Week, 1=Day, 2=Month (default 0)
  defaultLiabilityInsurance?: string; // insurance ID from Insurance table (type=0)
  defaultFullCoverageInsurance?: string; // insurance ID from Insurance table (type=1)
  defaultPricePerWeek?: number;       // default rental price per week for new cars (default 400)
  defaultExteriorColor?: string;      // default exterior color for new cars (default "Black")
  defaultInteriorColor?: string;      // default interior color for new cars (default "Black")

  /** Hours before booking end to send owner reminder (0 = disabled). Read by BackgroundService.BookingEndReminderObserver. */
  bookingEndReminderHoursBefore?: number;
  /** Send SMS to owner as part of the pre-end reminder. */
  bookingEndReminderSms?: boolean;
  /** Send email to owner as part of the pre-end reminder. */
  bookingEndReminderEmail?: boolean;

  /* ── Collecting unpaid bookings from DRIVERS ────────────────────────────────────────────────
   * A configurable stretch after a booking ends, whatever its card charges never collected
   * (tolls, registration fees, violations) is billed to the driver as one Stripe invoice —
   * split the standard way, so the owner's share reaches their connected account directly —
   * and then chased by letters that come from the OWNER and name nobody else.
   * Read by BackgroundService.PaymentReminderObserver, which also needs the host-level
   * Settings:PaymentRemindersEnabled switch. Both are off by default. */

  /** Master switch. Default false; turning it on also covers bookings that already finished. */
  paymentReminderEnabled?: boolean;
  /** Days after the booking ENDS before the driver is invoiced. Default 30; 0 bills immediately. */
  paymentInvoiceAfterBookingEndDays?: number;
  /** Days after the invoice was issued before the first reminder. Default 3. */
  paymentReminderFirstAfterDays?: number;
  /** Days between one reminder and the next. Floor of 1 — a same-day repeat is a resend loop. Default 7. */
  paymentReminderIntervalDays?: number;
  /** Total reminders per invoice including the first; the last is worded as a final notice. Max 10, default 3. */
  paymentReminderCount?: number;
  /** Email the driver, with the itemised invoice attached as a PDF. Default true. */
  paymentReminderEmail?: boolean;
  /** Also text the driver (billed per message). Default false. */
  paymentReminderSms?: boolean;
  /** Bookings owing less than this are not invoiced. Default 1.00. */
  paymentReminderMinAmount?: number;

  /** Turo host email used for platform-side login. */
  turoLogin?: string;
  /** Turo host password used for platform-side login. */
  turoPassword?: string;
  /** Persistent UUID v4 sent as Turo cookie td_dv_uid (mirrors mobile-app deviceInfoId). Auto-generated. */
  turoDeviceId?: string;

  /** GPS tracker alert delivery preferences. */
  gpsAlerts?: GpsAlertsSettings;

  gui?: GuiSettings;
  [key: string]: unknown;
}

/** Resolved delivery method: messageDeliveryMethod ?? smsDeliveryMethod ?? 'twilio' */
export function getEffectiveDeliveryMethod(settings: OwnerSettingsJSON): string {
  return settings.messageDeliveryMethod || settings.smsDeliveryMethod || 'twilio';
}

/** True when the owner asked for SMS *and* email rather than a single channel. */
export function usesAllDeliveryChannels(settings: OwnerSettingsJSON): boolean {
  return getEffectiveDeliveryMethod(settings) === 'both';
}

/**
 * The admin-only "book without a card" permission, as three separate answers:
 *  - `allowed`         what is in force right now (this is the one booking screens act on)
 *  - `stored`          what this owner was granted
 *  - `globallyEnabled` whether the deployment arms the feature at all (BookingWithoutCard__Enabled)
 *
 * They come apart when a permission was granted on an app that does not arm the feature — which must read
 * as "granted, not in force", never as a plain "off".
 */
export interface BookingWithoutCardSetting {
  allowed: boolean;
  stored: boolean;
  globallyEnabled: boolean;
}

/** What every failure answers: a card is required, which is the pre-existing behaviour. */
const CARD_REQUIRED: BookingWithoutCardSetting = { allowed: false, stored: false, globallyEnabled: false };

export const ownerSettingsApi = {
  /**
   * GET /OwnerSettings/{userId}
   * Returns { userId, settings: string (JSON) }
   */
  getSettings: async (userId: string): Promise<OwnerSettingsJSON> => {
    try {
      const response = await apiClient.get<{ result: { userId: string; settings: string } }>(
        `/OwnerSettings/${userId}`
      );
      const raw = response.data.result?.settings || '{}';
      return JSON.parse(raw) as OwnerSettingsJSON;
    } catch {
      return {};
    }
  },

  /**
   * PUT /OwnerSettings/{userId}
   * Saves { settings: string (JSON) }
   */
  // Whether this owner may book a driver whose card is missing, expired or declined.
  //
  // The value lives in the same settings blob as everything else, but it is NOT part of
  // OwnerSettingsJSON on purpose: the settings page round-trips that object on save, and this key is
  // admin-only — the backend puts the stored value back on any save that did not come from an admin.
  // Read by the owner's own booking screens (they have to know whether to ask for a card at all) and
  // by the admin card that sets it.
  //
  // Three values, because "off" has three meanings: `allowed` is what is actually in force, `stored` is
  // what this owner was granted, and `globallyEnabled` is the deployment-level safety catch
  // (BookingWithoutCard__Enabled). Any failure answers "a card is required" — always ask, as before.
  getBookingWithoutCard: async (ownerId: string): Promise<BookingWithoutCardSetting> => {
    try {
      const response = await apiClient.get<{ result: BookingWithoutCardSetting }>(
        `/OwnerSettings/booking-without-card/${ownerId}`
      );
      return response.data.result ?? CARD_REQUIRED;
    } catch {
      return CARD_REQUIRED;
    }
  },

  // Admin-only: grant or withdraw the permission. The backend audits the change and re-gates that
  // owner's in-progress bookings straight away. Errors are surfaced, not swallowed — an admin has to
  // see that the change did not take.
  setBookingWithoutCard: async (ownerId: string, allowed: boolean): Promise<boolean> => {
    const response = await apiClient.put<{ result: boolean }>(
      `/OwnerSettings/admin/booking-without-card/${ownerId}`,
      { allowed }
    );
    return response.data.result ?? false;
  },

  saveSettings: async (userId: string, settings: OwnerSettingsJSON): Promise<boolean> => {
    try {
      const response = await apiClient.put<{ result: boolean }>(
        `/OwnerSettings/${userId}`,
        { settings: JSON.stringify(settings) }
      );
      return response.data.result;
    } catch {
      return false;
    }
  },
};
